import { PortalInstrumentsTableProps } from '@/components/portal-modules/PortalInstrumentsTable';
import { defaultColumn } from '@/config/config';
import {
    getCfdFuturesInstrumentColumns,
    getCommodityCFDInstrumentColumns,
    getCryptoCFDInstrumentColumns,
    getCryptoSpotInstrumentColumns,
    getFXSpotInstrumentColumns,
    getIndexCFDInstrumentColumns,
    getRollingFXInstrumentColumns,
    InstrumentRow
} from '@/helpers/portalInstrumentsHelper';
import {
    ColumnDef,
    filterFns,
    getCoreRowModel,
    getFacetedMinMaxValues,
    getFacetedRowModel,
    getFacetedUniqueValues,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { useDateFormatter } from 'react-aria';
import { useDebouncedValue } from './useDebouncedValue';
import useLanguage from './useLanguage';
import { fuzzyGlobalFilter } from './useOpenPositionTable';

export interface PortalInstrumentsRow extends InstrumentRow {}

interface UsePortalInstrumentsTableProps extends PortalInstrumentsTableProps {}

const usePortalInstrumentsTable = (props: UsePortalInstrumentsTableProps) => {
    const { filters, data, pagination, isLoading } = props;
    const { instrumentType } = filters;

    const lang = useLanguage();
    const [sorting, setSorting] = useState<SortingState>([]);
    const [globalFilter, setGlobalFilter] = useState<string>('');
    const [columnSizing, setColumnSizing] = useState<Record<string, number>>({});

    const dateFormatter = useDateFormatter({
        timeZone: 'UTC'
    });

    const columns: ColumnDef<InstrumentRow>[] = useMemo(() => {
        if (!isLoading && data) {
            switch (instrumentType.value) {
                case 'Crypto CFD':
                    return getCryptoCFDInstrumentColumns(lang);
                case 'Equity Index CFD':
                    return getIndexCFDInstrumentColumns(lang);
                case 'Commodity CFD':
                    return getCommodityCFDInstrumentColumns(lang);
                case 'Rolling Fx':
                    return getRollingFXInstrumentColumns(lang);
                case 'Crypto Spot':
                    return getCryptoSpotInstrumentColumns(lang);
                case 'Futures CFD':
                    return getCfdFuturesInstrumentColumns(lang);
                default:
                    return getFXSpotInstrumentColumns(lang);
            }
        } else return [];
    }, [lang, data, instrumentType, isLoading]);

    const formattedPortalInstruments: PortalInstrumentsRow[] = useMemo(() => {
        if (data) return data;
        return [];
    }, [data]);

    const tableInstance = useReactTable({
        data: formattedPortalInstruments,
        columns,
        state: { globalFilter, columnSizing, sorting },
        defaultColumn: { ...defaultColumn, filterFn: filterFns.includesString },
        pageCount: data ? Math.ceil(data.length / pagination.pageSize) : undefined,
        enableColumnResizing: true,
        columnResizeMode: 'onChange',
        globalFilterFn: (row, columnId, filterValue) => {
            return fuzzyGlobalFilter(row, columnId, filterValue, dateFormatter);
        },
        onGlobalFilterChange: setGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
        getFacetedMinMaxValues: getFacetedMinMaxValues(),
        getPaginationRowModel: getPaginationRowModel(),
        onColumnSizingChange: setColumnSizing,
        onSortingChange: setSorting,
        enableRowSelection: true
    });

    const [debouncedTableInstance] = useDebouncedValue(tableInstance, 500);

    return { debouncedTableInstance, columns, columnSizing, sorting, formattedPortalInstruments, globalFilter };
};

export default usePortalInstrumentsTable;
